import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, InputNumber, Popconfirm, message, Space, Switch, Tag, Card, Row, Col, Statistic, Collapse, Tabs, DatePicker, Grid, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SettingOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { carilerAPI, islemlerAPI, satislarAPI, alislarAPI, cariBakiyeAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, paraInputProps } from '../utils/format';
import { bosSecenekliOptions, BOS_FILTRE } from '../utils/filtre';

function Cariler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [cariler, setCariler] = useState([]);
  const [hamSatislar, setHamSatislar] = useState([]);
  const [hamGiderler, setHamGiderler] = useState([]);
  const [hamOdemeler, setHamOdemeler] = useState([]);
  const [hamTahsilatlar, setHamTahsilatlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreAdIds, setFiltreAdIds] = useState([]);
  const [filtreTipIds, setFiltreTipIds] = useState([]);
  const [pasifleriGoster, setPasifleriGoster] = useState(false);
  const [filtreBakiyeOp, setFiltreBakiyeOp] = useState(null);
  const [filtreBakiyeDeger, setFiltreBakiyeDeger] = useState(null);
  const [filtreBakiyeTarihi, setFiltreBakiyeTarihi] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [kolonAyarModalOpen, setKolonAyarModalOpen] = useState(false);
  const [gorunenKolonlar, setGorunenKolonlar] = useState(['ad', 'durum', 'tip', 'bakiye', 'islem']);
  const [detayModalOpen, setDetayModalOpen] = useState(false);
  const [detayCari, setDetayCari] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);
  const [detayData, setDetayData] = useState({ alislar: [], satislar: [], tahsilatlar: [], odemeler: [] });
  const [detayBakiye, setDetayBakiye] = useState(null);
  const [detayFiltreTarihBas, setDetayFiltreTarihBas] = useState(null);
  const [detayFiltreTarihBit, setDetayFiltreTarihBit] = useState(null);
  const [detayFiltreTutarOp, setDetayFiltreTutarOp] = useState(null);
  const [detayFiltreTutarDeger, setDetayFiltreTutarDeger] = useState(null);
  const [bakiyeTarihi, setBakiyeTarihi] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { cariTipiOptions: cariTipleri } = useLookups();

  // Cariye göre toplam — D verilirse yalnızca (belge) tarihi ≤ D olan kayıtlar toplanır.
  const topla = (liste, cariKey = 'cari_id', D = null) => {
    const map = {};
    (liste || []).forEach(item => {
      const id = item[cariKey];
      if (!id) return;
      if (D && dayjs(item.tarih).isAfter(D, 'day')) return;
      map[id] = (map[id] || 0) + (Number(item.toplam_tutar ?? item.tutar) || 0);
    });
    return map;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [carilerRes, odemelerRes, tahsilatlarRes, satislarRes, giderRes] = await Promise.all([
        carilerAPI.getAll(),
        islemlerAPI.getAll({ islem_tipi_id: 9 }),
        islemlerAPI.getAll({ islem_tipi_id: 10 }),
        satislarAPI.getAll(),
        alislarAPI.getAll(),
      ]);
      setCariler(carilerRes.data || []);
      setHamOdemeler(odemelerRes.data || []);
      setHamTahsilatlar(tahsilatlarRes.data || []);
      setHamSatislar(satislarRes.data || []);
      setHamGiderler(giderRes.data || []);
    } catch (err) {
      message.error('Cariler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Cari listesi + bakiye — filtreBakiyeTarihi verilirse bakiye o tarihe kadar (belge tarihi ≤ D) hesaplanır.
  // İşaret kuralı: satış − alış + ödeme − tahsilat (pozitif = cari bize borçlu).
  const data = useMemo(() => {
    const D = filtreBakiyeTarihi;
    const odemelerMap = topla(hamOdemeler, 'cari_id', D);
    const tahsilatlarMap = topla(hamTahsilatlar, 'cari_id', D);
    const satislarMap = topla(hamSatislar, 'cari_id', D);
    const giderMap = topla(hamGiderler, 'cari_id', D);
    return (cariler || []).map(c => {
      const odeme = odemelerMap[c.id] || 0;
      const tahsilat = tahsilatlarMap[c.id] || 0;
      const satis = satislarMap[c.id] || 0;
      const gider = giderMap[c.id] || 0;
      return {
        ...c,
        toplam_odeme: odeme,
        toplam_tahsilat: tahsilat,
        toplam_satis: satis,
        toplam_gider: gider,
        bakiye: satis - gider + odeme - tahsilat,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cariler, hamOdemeler, hamTahsilatlar, hamSatislar, hamGiderler, filtreBakiyeTarihi]);

  useEffect(() => {
    const detayId = searchParams.get('detay');
    if (detayId && data.length > 0) {
      const record = data.find(c => String(c.id) === detayId);
      if (record) {
        handleDetayAc(record);
        navigate('/cariler', { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  const handleSubmit = async (values) => {
    const payload = { ...values, aktif: values.aktif !== false ? 1 : 0 };
    try {
      if (editingRecord) {
        await carilerAPI.update(editingRecord.id, payload);
        message.success('Cari güncellendi');
      } else {
        await carilerAPI.create(payload);
        const musteriSecildi = payload.tip_idler && payload.tip_idler.includes(2);
        if (musteriSecildi) {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
          message.info('Müşteri seçimli cari eklediniz. Müşteri için gerekli bilgileri doldurmak üzere yönlendiriliyorsunuz.');
          navigate(`/musteriler?duzenle=${encodeURIComponent(values.ad)}`);
          return;
        }
        message.success('Cari eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Cari güncellenemedi: ' : 'Cari eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ad: record.ad,
      tip_idler: record.tip_idler,
      telefon: record.telefon,
      email: record.email,
      adres: record.adres,
      aktif: record.aktif !== 0,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await carilerAPI.remove(id);
      message.success('Cari silindi');
      fetchData();
    } catch (err) {
      message.error('Cari silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleDetayAc = async (record) => {
    // Detay başlığı her zaman tam geçmişi (güncel) gösterir — liste tarih filtresinden bağımsız.
    // Tarihli kırılım için detayın kendi "Bakiye tarihi" seçicisi kullanılır.
    const satis = topla(hamSatislar, 'cari_id')[record.id] || 0;
    const gider = topla(hamGiderler, 'cari_id')[record.id] || 0;
    const odeme = topla(hamOdemeler, 'cari_id')[record.id] || 0;
    const tahsilat = topla(hamTahsilatlar, 'cari_id')[record.id] || 0;
    setDetayCari({
      ...record,
      toplam_satis: satis,
      toplam_gider: gider,
      toplam_odeme: odeme,
      toplam_tahsilat: tahsilat,
      bakiye: satis - gider + odeme - tahsilat,
    });
    setDetayModalOpen(true);
    setDetayLoading(true);
    setDetayFiltreTarihBas(null);
    setDetayFiltreTarihBit(null);
    setDetayFiltreTutarOp(null);
    setDetayFiltreTutarDeger(null);
    setBakiyeTarihi(null);
    setDetayBakiye(null);
    try {
      const [satisRes, giderRes, odemelerRes, tahsilatlarRes, bakiyeRes] = await Promise.all([
        satislarAPI.getAll(),
        alislarAPI.getAll(),
        islemlerAPI.getAll({ islem_tipi_id: 9 }),
        islemlerAPI.getAll({ islem_tipi_id: 10 }),
        cariBakiyeAPI.getOzet(record.id).catch(() => ({ data: null })),
      ]);
      setDetayData({
        satislar: (satisRes.data || []).filter(s => s.cari_id === record.id),
        alislar: (giderRes.data || []).filter(g => g.cari_id === record.id),
        odemeler: (odemelerRes.data || []).filter(o => o.cari_id === record.id),
        tahsilatlar: (tahsilatlarRes.data || []).filter(t => t.cari_id === record.id),
      });
      setDetayBakiye(bakiyeRes.data || null);
    } catch {
      message.error('Cari detayları yüklenemedi');
    } finally {
      setDetayLoading(false);
    }
  };

  const detayFiltrele = (liste) => {
    return liste.filter(item => {
      if (detayFiltreTarihBas && dayjs(item.tarih).isBefore(detayFiltreTarihBas, 'day')) return false;
      if (detayFiltreTarihBit && dayjs(item.tarih).isAfter(detayFiltreTarihBit, 'day')) return false;
      if (detayFiltreTutarOp && detayFiltreTutarDeger != null) {
        const tutar = Number(item.toplam_tutar || item.tutar || 0);
        const deger = Number(detayFiltreTutarDeger);
        if (detayFiltreTutarOp === 'gt' && !(tutar > deger)) return false;
        if (detayFiltreTutarOp === 'gte' && !(tutar >= deger)) return false;
        if (detayFiltreTutarOp === 'lt' && !(tutar < deger)) return false;
        if (detayFiltreTutarOp === 'lte' && !(tutar <= deger)) return false;
        if (detayFiltreTutarOp === 'eq' && !(tutar === deger)) return false;
      }
      return true;
    });
  };

  const detayFiltreAktifMi = detayFiltreTarihBas || detayFiltreTarihBit || detayFiltreTutarOp;
  const detayFiltreTemizle = () => { setDetayFiltreTarihBas(null); setDetayFiltreTarihBit(null); setDetayFiltreTutarOp(null); setDetayFiltreTutarDeger(null); };

  // Seçilen tarihe kadar (o gün dahil) net bakiye — ham işlemlerden hesaplanır.
  // İşaret kuralı liste bakiyesiyle aynı: satış − alış + ödeme − tahsilat (pozitif = cari bize borçlu).
  const bakiyeTarihliNet = useMemo(() => {
    if (!bakiyeTarihi) return null;
    const toplaTarihli = (liste) => (liste || []).reduce((t, item) => {
      if (dayjs(item.tarih).isAfter(bakiyeTarihi, 'day')) return t;
      return t + (Number(item.toplam_tutar ?? item.tutar) || 0);
    }, 0);
    const satis = toplaTarihli(detayData.satislar);
    const alis = toplaTarihli(detayData.alislar);
    const odeme = toplaTarihli(detayData.odemeler);
    const tahsilat = toplaTarihli(detayData.tahsilatlar);
    return satis - alis + odeme - tahsilat;
  }, [bakiyeTarihi, detayData]);

  const filteredData = useMemo(() => {
    return data.filter(c => {
      if (aramaMetni && !(c.ad || '').toLowerCase().includes(aramaMetni.toLowerCase())) return false;
      if (filtreAdIds.length > 0 && !filtreAdIds.includes(c.id)) return false;
      if (!pasifleriGoster && c.aktif === 0) return false;
      if (filtreTipIds.length > 0) {
        const tipler = (c.tip_idler || []);
        // Tipi hiç olmayan cariler "(Boş)" seçimiyle eşleşir
        const uyuyor = tipler.length === 0
          ? filtreTipIds.includes(BOS_FILTRE)
          : filtreTipIds.some(id => tipler.includes(id));
        if (!uyuyor) return false;
      }
      if (filtreBakiyeOp && filtreBakiyeDeger != null) {
        const bakiye = Number(c.bakiye || 0), deger = Number(filtreBakiyeDeger);
        if (filtreBakiyeOp === 'gt' && !(bakiye > deger)) return false;
        if (filtreBakiyeOp === 'gte' && !(bakiye >= deger)) return false;
        if (filtreBakiyeOp === 'lt' && !(bakiye < deger)) return false;
        if (filtreBakiyeOp === 'lte' && !(bakiye <= deger)) return false;
        if (filtreBakiyeOp === 'eq' && !(bakiye === deger)) return false;
        if (filtreBakiyeOp === 'ne' && !(bakiye !== deger)) return false;
      }
      return true;
    });
  }, [data, aramaMetni, filtreAdIds, filtreTipIds, pasifleriGoster, filtreBakiyeOp, filtreBakiyeDeger]);

  const cariAdOptions = useMemo(() =>
    [...data].sort((a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr')).map(c => ({ value: c.id, label: c.ad })),
    [data]
  );

  const cariTipiFilterOptions = useMemo(
    () => bosSecenekliOptions(cariTipleri, data, c => ((c.tip_idler || []).length ? 1 : null)),
    [cariTipleri, data]
  );

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, bakiye: 0, satis: 0, gider: 0 };
    filteredData.forEach(c => { t.bakiye += Number(c.bakiye || 0); t.satis += Number(c.toplam_satis || 0); t.gider += Number(c.toplam_gider || 0); });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreAdIds.length > 0 || filtreTipIds.length > 0 || pasifleriGoster || filtreBakiyeOp || filtreBakiyeTarihi;
  const filtreTemizle = () => { setFiltreAdIds([]); setFiltreTipIds([]); setPasifleriGoster(false); setFiltreBakiyeOp(null); setFiltreBakiyeDeger(null); setFiltreBakiyeTarihi(null); };

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');
  const numSorter = (key) => (a, b) => (a[key] || 0) - (b[key] || 0);
  const tutarRender = (v) => formatPara(v || 0);

  const tumKolonlar = [
    { key: 'ad', title: 'Ad', dataIndex: 'ad', sorter: strSorter('ad'), render: (v, r) => <a onClick={() => handleDetayAc(r)} style={{ color: r.aktif === 0 ? '#999' : undefined }}>{v}</a> },
    { key: 'durum', title: 'Durum', dataIndex: 'aktif', width: 90, align: 'center', responsive: ['sm'], render: (v) => v === 0 ? <Tag color="red">Pasif</Tag> : <Tag color="green">Aktif</Tag>, sorter: (a, b) => (a.aktif || 0) - (b.aktif || 0) },
    { key: 'tip', title: 'Tip', dataIndex: 'tip', sorter: strSorter('tip'), responsive: ['md'] },
    { key: 'telefon', title: 'Telefon', dataIndex: 'telefon', sorter: strSorter('telefon'), responsive: ['md'] },
    { key: 'email', title: 'Email', dataIndex: 'email', sorter: strSorter('email'), responsive: ['lg'] },
    { key: 'adres', title: 'Adres', dataIndex: 'adres', sorter: strSorter('adres'), responsive: ['lg'] },
    { key: 'toplam_satis', title: 'Satışlar', dataIndex: 'toplam_satis', width: 130, align: 'right', responsive: ['lg'], render: tutarRender, sorter: numSorter('toplam_satis') },
    { key: 'toplam_gider', title: 'Alışlar', dataIndex: 'toplam_gider', width: 130, align: 'right', responsive: ['lg'], render: tutarRender, sorter: numSorter('toplam_gider') },
    { key: 'toplam_tahsilat', title: 'Tahsilatlar', dataIndex: 'toplam_tahsilat', width: 140, align: 'right', responsive: ['lg'], render: tutarRender, sorter: numSorter('toplam_tahsilat') },
    { key: 'toplam_odeme', title: 'Ödemeler', dataIndex: 'toplam_odeme', width: 130, align: 'right', responsive: ['lg'], render: tutarRender, sorter: numSorter('toplam_odeme') },
    { key: 'bakiye', title: filtreBakiyeTarihi ? `Bakiye (${dayjs(filtreBakiyeTarihi).format('DD.MM.YYYY')})` : 'Bakiye', dataIndex: 'bakiye', width: isMobile ? 90 : 150, align: 'right', render: (v) => <span style={{ color: v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : undefined, fontWeight: 600 }}>{tutarRender(v)}</span>, sorter: numSorter('bakiye') },
    {
      key: 'islem', title: 'İşlem',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
          <Popconfirm title="Bu cariyi silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const kolonSecenekleri = tumKolonlar
    .filter(c => c.key !== 'islem')
    .map(c => ({ label: c.title, value: c.key }));

  const columns = tumKolonlar.filter(c => gorunenKolonlar.includes(c.key));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Cariler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          {screens.lg && (
            <Button icon={<SettingOutlined />} onClick={() => setKolonAyarModalOpen(true)}>Kolonlar</Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} title="Yeni Cari">
            {!isMobile && 'Yeni Cari'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Cari Adı</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Cari seçin..." options={cariAdOptions} value={filtreAdIds} onChange={setFiltreAdIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tip</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Tip seçin..." options={cariTipiFilterOptions} value={filtreTipIds} onChange={setFiltreTipIds} maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Durum</label>
              <div style={{ paddingTop: 6 }}>
                <Checkbox checked={pasifleriGoster} onChange={(e) => setPasifleriGoster(e.target.checked)}>Pasifleri de göster</Checkbox>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Bakiye Filtresi</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreBakiyeOp} onChange={setFiltreBakiyeOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }, { value: 'ne', label: '≠' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreBakiyeDeger} onChange={setFiltreBakiyeDeger} />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Bakiye Tarihi (… tarihine kadar)</label>
              <DatePicker style={{ width: '100%' }} value={filtreBakiyeTarihi} onChange={setFiltreBakiyeTarihi} format="DD.MM.YYYY" placeholder="Tümü (bugüne kadar)" allowClear />
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Bakiye, belge tarihine göre seçilen güne kadar hesaplanır.</div>
            </Col>
          </Row>
        </Card>
      )}]} />

      {filtreAktifMi && (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={4}><Statistic title="Kayıt Sayısı" value={toplamlar.adet} /></Col>
            <Col xs={12} sm={8} md={4}><Statistic title="Toplam Satış" value={toplamlar.satis} precision={2} suffix="₺" /></Col>
            <Col xs={12} sm={8} md={4}><Statistic title="Toplam Alış" value={toplamlar.gider} precision={2} suffix="₺" /></Col>
            <Col xs={12} sm={8} md={4}><Statistic title={filtreBakiyeTarihi ? `Toplam Bakiye (${dayjs(filtreBakiyeTarihi).format('DD.MM.YYYY')})` : 'Toplam Bakiye'} value={toplamlar.bakiye} precision={2} suffix="₺" valueStyle={{ color: toplamlar.bakiye >= 0 ? '#3f8600' : '#cf1322' }} /></Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal title={editingRecord ? 'Cari Düzenle' : 'Yeni Cari'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()}
        width={isMobile ? '95vw' : 520}
        style={isMobile ? { top: 10, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 12 } } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tip_idler" label="Tip" rules={[{ required: true }]}>
            <Select mode="multiple" options={cariTipleri} placeholder="Bir veya birden fazla tip seçin" />
          </Form.Item>
          <Form.Item name="telefon" label="Telefon">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>
          <Form.Item name="adres" label="Adres">
            <Input />
          </Form.Item>
          <Form.Item name="aktif" label="Durum" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Kolon Ayarları"
        open={kolonAyarModalOpen}
        onCancel={() => setKolonAyarModalOpen(false)}
        onOk={() => setKolonAyarModalOpen(false)}
        width={360}
      >
        <p style={{ color: '#888', marginBottom: 12 }}>Tabloda görmek istediğiniz kolonları seçin:</p>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          value={gorunenKolonlar.filter(k => k !== 'islem')}
          onChange={(val) => setGorunenKolonlar([...val, 'islem'])}
          options={kolonSecenekleri}
          placeholder="Kolon seçin"
        />
      </Modal>
      {/* Cari Detay Modalı */}
      <Modal
        title={detayCari ? `${detayCari.ad} — Cari Hesap Detayı` : 'Cari Detay'}
        open={detayModalOpen}
        onCancel={() => { setDetayModalOpen(false); setDetayCari(null); }}
        footer={<Button onClick={() => { setDetayModalOpen(false); setDetayCari(null); }}>Kapat</Button>}
        width={950}
      >
        {detayCari && (
          <div style={{ marginBottom: 12 }}>
            <Row gutter={[16, 12]}>
              <Col xs={12} sm={12} md={8} lg={5}><Statistic title="Alışlar" value={detayCari.toplam_gider} precision={2} suffix="₺" /></Col>
              <Col xs={12} sm={12} md={8} lg={5}><Statistic title="Satışlar" value={detayCari.toplam_satis} precision={2} suffix="₺" /></Col>
              <Col xs={12} sm={12} md={8} lg={5}><Statistic title="Tahsilatlar" value={detayCari.toplam_tahsilat} precision={2} suffix="₺" /></Col>
              <Col xs={12} sm={12} md={8} lg={5}><Statistic title="Ödemeler" value={detayCari.toplam_odeme} precision={2} suffix="₺" /></Col>
              <Col xs={12} sm={12} md={8} lg={4}><Statistic title="Bakiye (Güncel)" value={detayCari.bakiye} precision={2} suffix="₺" valueStyle={{ color: detayCari.bakiye > 0 ? '#3f8600' : detayCari.bakiye < 0 ? '#cf1322' : undefined }} /></Col>
            </Row>
            <Row gutter={[16, 8]} align="middle" style={{ marginTop: 8, background: '#fafafa', borderRadius: 6, padding: '8px 12px' }}>
              <Col>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Bakiye tarihi (… tarihine kadar)</label>
                <DatePicker size="small" value={bakiyeTarihi} onChange={setBakiyeTarihi} format="DD.MM.YYYY" placeholder="Tarih seçin" allowClear />
              </Col>
              {bakiyeTarihliNet != null && (
                <Col>
                  <Statistic
                    title={`${dayjs(bakiyeTarihi).format('DD.MM.YYYY')} tarihine kadar bakiye`}
                    value={bakiyeTarihliNet}
                    precision={2}
                    suffix="₺"
                    valueStyle={{ color: bakiyeTarihliNet > 0 ? '#3f8600' : bakiyeTarihliNet < 0 ? '#cf1322' : undefined }}
                  />
                </Col>
              )}
              <Col flex="auto" style={{ textAlign: 'right', fontSize: 11, color: '#999' }}>
                İşlem (belge) tarihine göre; o güne kadar oluşan satış/alış/tahsilat/ödeme toplamı.
              </Col>
            </Row>
          </div>
        )}

        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 8]} align="middle">
            <Col>
              <label style={{ fontSize: 12, color: '#666' }}>Tarih Başlangıç</label>
              <DatePicker size="small" style={{ display: 'block' }} value={detayFiltreTarihBas} onChange={setDetayFiltreTarihBas} format="DD.MM.YYYY" placeholder="Başlangıç" />
            </Col>
            <Col>
              <label style={{ fontSize: 12, color: '#666' }}>Tarih Bitiş</label>
              <DatePicker size="small" style={{ display: 'block' }} value={detayFiltreTarihBit} onChange={setDetayFiltreTarihBit} format="DD.MM.YYYY" placeholder="Bitiş" />
            </Col>
            <Col>
              <label style={{ fontSize: 12, color: '#666' }}>Tutar</label>
              <Space.Compact style={{ display: 'block' }}>
                <Select size="small" style={{ width: 60 }} placeholder="Op" value={detayFiltreTutarOp} onChange={setDetayFiltreTutarOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} size="small" style={{ width: 90 }} placeholder="Tutar" value={detayFiltreTutarDeger} onChange={setDetayFiltreTutarDeger} min={0} />
              </Space.Compact>
            </Col>
            {detayFiltreAktifMi && (
              <Col>
                <div style={{ marginTop: 16 }}>
                  <Button size="small" icon={<ClearOutlined />} onClick={detayFiltreTemizle}>Temizle</Button>
                </div>
              </Col>
            )}
          </Row>
        </Card>

        <Tabs items={[
          {
            key: 'alislar',
            label: `Alışlar (${detayFiltrele(detayData.alislar).length})`,
            children: (
              <Table
                size="small"
                loading={detayLoading}
                dataSource={detayFiltrele(detayData.alislar)}
                rowKey="id"
                pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] }}
                onRow={(record) => ({ onClick: () => navigate(`/alislar?duzenle=${record.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: 100, render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-', sorter: (a, b) => new Date(a.tarih || 0) - new Date(b.tarih || 0), defaultSortOrder: 'descend' },
                  { title: 'Fatura No', dataIndex: 'fatura_no', key: 'fatura_no' },
                  { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama' },
                  { title: 'Toplam', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: 120, align: 'right', render: (v) => formatPara(v || 0) },
                ]}
              />
            ),
          },
          {
            key: 'satislar',
            label: `Satışlar (${detayFiltrele(detayData.satislar).length})`,
            children: (
              <Table
                size="small"
                loading={detayLoading}
                dataSource={detayFiltrele(detayData.satislar)}
                rowKey="id"
                pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] }}
                onRow={(record) => ({ onClick: () => navigate(`/satislar?duzenle=${record.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: 100, render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-', sorter: (a, b) => new Date(a.tarih || 0) - new Date(b.tarih || 0), defaultSortOrder: 'descend' },
                  { title: 'Gelir Tipi', dataIndex: 'gelir_tipi', key: 'gelir_tipi' },
                  { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama' },
                  { title: 'Toplam', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: 120, align: 'right', render: (v) => formatPara(v || 0) },
                ]}
              />
            ),
          },
          {
            key: 'odemeler',
            label: `Ödemeler (${detayFiltrele(detayData.odemeler).length})`,
            children: (
              <Table
                size="small"
                loading={detayLoading}
                dataSource={detayFiltrele(detayData.odemeler)}
                rowKey="id"
                pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] }}
                onRow={(record) => ({ onClick: () => navigate(`/odemeler?duzenle=${record.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: 100, render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-', sorter: (a, b) => new Date(a.tarih || 0) - new Date(b.tarih || 0), defaultSortOrder: 'descend' },
                  { title: 'Kasa', dataIndex: 'kasa_ad', key: 'kasa_ad' },
                  { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama' },
                  { title: 'Tutar', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: 120, align: 'right', render: (v) => formatPara(v || 0) },
                ]}
              />
            ),
          },
          {
            key: 'tahsilatlar',
            label: `Tahsilatlar (${detayFiltrele(detayData.tahsilatlar).length})`,
            children: (
              <Table
                size="small"
                loading={detayLoading}
                dataSource={detayFiltrele(detayData.tahsilatlar)}
                rowKey="id"
                pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20'] }}
                onRow={(record) => ({ onClick: () => navigate(`/tahsilatlar?duzenle=${record.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Tarih', dataIndex: 'tarih', key: 'tarih', width: 100, render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-', sorter: (a, b) => new Date(a.tarih || 0) - new Date(b.tarih || 0), defaultSortOrder: 'descend' },
                  { title: 'Kasa', dataIndex: 'kasa_ad', key: 'kasa_ad' },
                  { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama' },
                  { title: 'Tutar', dataIndex: 'toplam_tutar', key: 'toplam_tutar', width: 120, align: 'right', render: (v) => formatPara(v || 0) },
                ]}
              />
            ),
          },
          {
            key: 'vade-avans',
            label: 'Vade & Avans',
            children: detayBakiye ? (
              <div>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={[16, 12]}>
                    <Col xs={12} md={6}><Statistic title="Açık Satış (Tahsil edilecek)" value={detayBakiye.bakiye?.acikSatis || 0} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} /></Col>
                    <Col xs={12} md={6}><Statistic title="Açık Alış (Ödenecek)" value={detayBakiye.bakiye?.acikAlis || 0} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
                    <Col xs={12} md={6}><Statistic title="Net (Açık)" value={detayBakiye.bakiye?.net || 0} precision={2} suffix="₺" /></Col>
                    <Col xs={12} md={6}>
                      <Statistic title="Avans" value={(detayBakiye.avans?.tahsilatAvansToplam || 0) - (detayBakiye.avans?.odemeAvansToplam || 0)} precision={2} suffix="₺" />
                      <div style={{ fontSize: 11, color: '#666' }}>
                        Tahsilat avans: {formatPara(detayBakiye.avans?.tahsilatAvansToplam || 0)} | Ödeme avans: {formatPara(detayBakiye.avans?.odemeAvansToplam || 0)}
                      </div>
                    </Col>
                  </Row>
                </Card>
                <Card size="small" title="Yaşlandırma (gün)" style={{ marginBottom: 12 }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <strong>Satış (Alacak):</strong>
                      <div style={{ fontSize: 13 }}>
                        0-30 gün: <strong>{formatPara(detayBakiye.yaslandirma?.satis?.gun_0_30 || 0)}</strong> |
                        30-60: <strong>{formatPara(detayBakiye.yaslandirma?.satis?.gun_30_60 || 0)}</strong> |
                        60-90: <strong>{formatPara(detayBakiye.yaslandirma?.satis?.gun_60_90 || 0)}</strong> |
                        90+: <strong style={{ color: '#cf1322' }}>{formatPara(detayBakiye.yaslandirma?.satis?.gun_90_plus || 0)}</strong>
                      </div>
                    </Col>
                    <Col span={12}>
                      <strong>Alış (Borç):</strong>
                      <div style={{ fontSize: 13 }}>
                        0-30: <strong>{formatPara(detayBakiye.yaslandirma?.alis?.gun_0_30 || 0)}</strong> |
                        30-60: <strong>{formatPara(detayBakiye.yaslandirma?.alis?.gun_30_60 || 0)}</strong> |
                        60-90: <strong>{formatPara(detayBakiye.yaslandirma?.alis?.gun_60_90 || 0)}</strong> |
                        90+: <strong style={{ color: '#cf1322' }}>{formatPara(detayBakiye.yaslandirma?.alis?.gun_90_plus || 0)}</strong>
                      </div>
                    </Col>
                  </Row>
                </Card>
                {(detayBakiye.avans?.tahsilatAvanslar?.length > 0 || detayBakiye.avans?.odemeAvanslar?.length > 0) && (
                  <Card size="small" title="Avans Detayı">
                    {detayBakiye.avans.tahsilatAvanslar.length > 0 && (
                      <>
                        <strong style={{ fontSize: 13 }}>Kullanılmamış Tahsilatlar:</strong>
                        <Table
                          size="small"
                          dataSource={detayBakiye.avans.tahsilatAvanslar}
                          rowKey="id"
                          pagination={false}
                          style={{ marginBottom: 8 }}
                          columns={[
                            { title: 'Tarih', dataIndex: 'tarih', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                            { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v) => formatPara(v) },
                            { title: 'Kullanılabilir', dataIndex: 'kullanilabilir', align: 'right', render: (v) => <strong>{formatPara(v)}</strong> },
                          ]}
                        />
                      </>
                    )}
                    {detayBakiye.avans.odemeAvanslar.length > 0 && (
                      <>
                        <strong style={{ fontSize: 13 }}>Kullanılmamış Ödemeler:</strong>
                        <Table
                          size="small"
                          dataSource={detayBakiye.avans.odemeAvanslar}
                          rowKey="id"
                          pagination={false}
                          columns={[
                            { title: 'Tarih', dataIndex: 'tarih', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-' },
                            { title: 'Tutar', dataIndex: 'tutar', align: 'right', render: (v) => formatPara(v) },
                            { title: 'Kullanılabilir', dataIndex: 'kullanilabilir', align: 'right', render: (v) => <strong>{formatPara(v)}</strong> },
                          ]}
                        />
                      </>
                    )}
                  </Card>
                )}
              </div>
            ) : <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>Veri yükleniyor veya yok</div>,
          },
        ]} />
      </Modal>
    </div>
  );
}

export default Cariler;